import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowUp, ArrowDown, Search, Star, ChevronsUpDown, Columns3, GripVertical, Bell } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { computeRR } from "@/lib/tradeMath";

// R:R on the Journal must reflect the *actual* fill-derived entry average (not
// the planned entry_price on the trade record) against the trade-setup stop.
// If no fills exist yet (idea-only trade) we hide the value entirely instead of
// falling back to the planned entry — that was the whole point of the request.
const computeJournalRR = (t) => {
  const actualEntry = t?.entry_avg_actual;
  if (actualEntry == null || t?.stop_loss == null) return null;
  // computeRR reads `trade.entry_price` + `trade.shares`; shares cancel in the
  // ratio so we pass a placeholder to satisfy the gate.
  return computeRR({ ...t, entry_price: actualEntry, shares: t?.shares ?? 1 });
};
import TradeReactions from "@/components/TradeReactions";
import { useAuth } from "@/context/AuthContext";
import { canSeeDollarStats } from "@/lib/permissions";

function fmtMoney(n) {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPrice(n) {
  if (n == null) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Total P&L for a trade row = realized (from FIFO of Open - Entry/Added/Trimmed
// phases) + unrealized on any still-open shares priced at the live quote.
function computeTotalPnl(t, quotes) {
  const realized = t?.realized_pnl ?? null;
  const openShares = t?.open_shares || 0;
  const openAvgCost = t?.open_avg_cost;
  const openSide = t?.open_side; // "long" | "short" | null
  const price = quotes?.[t?.ticker]?.price ?? null;
  let unrealized = null;
  if (openShares > 0 && openAvgCost != null && price != null && openSide) {
    unrealized = openSide === "long"
      ? (price - openAvgCost) * openShares
      : (openAvgCost - price) * openShares;
  }
  if (realized == null && unrealized == null) {
    return t?.pnl != null ? t.pnl : null;
  }
  return (realized || 0) + (unrealized || 0);
}

// Total P&L % = total_pnl / total_capital_deployed × 100
// where capital deployed is entry_avg × total opened shares (buys + open still-held).
// Falls back to trade.pnl_pct when we cannot derive from fills.
function computeTotalPnlPct(t, quotes) {
  const total = computeTotalPnl(t, quotes);
  if (total == null) return null;
  const entryAvg = t?.entry_avg ?? t?.entry_price;
  const openShares = t?.open_shares || 0;
  const openAvgCost = t?.open_avg_cost;
  // Total shares that were ever opened (long or short) = derived capital denominator.
  // For simple cases (BUY→SELL or BUY→BUY→SELL) this equals entry_shares.
  // We approximate capital deployed using entry_avg * (closed_shares + open_shares).
  // closed_shares comes from realized_pnl side (we can infer from open_avg_cost vs entry_avg
  // relationship, but simpler: fall back to trade.pnl_pct if we can't).
  // A stable heuristic: use entry_avg * (open_shares + inferred closed shares).
  // If the trade is fully closed and we have exit/entry, use realized as pct of entry cost.
  // Otherwise fall back to trade.pnl_pct when set.
  if (entryAvg && (openShares > 0 || t?.realized_pnl != null)) {
    // Estimate opened shares: prefer explicit trade.shares when present, else fall
    // back to open_shares (still-held) + any inferred from realized side.
    // Since realized comes from FIFO, we don't have an exact opened-shares count
    // on the row, so use trade.shares as denominator when set, else open_shares
    // + closing_fills-derived shares if available.
    let openedShares = t?.shares;
    if (!openedShares || openedShares <= 0) {
      const closingFills = (t?.closing_fills || []).filter((f) => (f.side === "SELL" && (t.direction || "long") === "long") || (f.side === "BUY" && t.direction === "short"));
      const closedFromFills = closingFills.reduce((a, f) => a + Number(f.shares || 0), 0);
      openedShares = openShares + closedFromFills;
    }
    if (openedShares > 0) {
      const capital = entryAvg * openedShares;
      if (capital > 0) return (total / capital) * 100;
    }
  }
  return t?.pnl_pct != null ? t.pnl_pct : null;
}
function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString();
}
function fmtRelative(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return "—";
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}
// Duration in days between trade entry (first opening fill) and either
// close time or now (for still-open trades). Returns null when no entry.
function computeDurationDays(t) {
  const entryIso = t?.entry_at;
  if (!entryIso) return null;
  const start = new Date(entryIso);
  if (isNaN(start)) return null;
  const endIso = t?.closed_at;
  const end = endIso ? new Date(endIso) : new Date();
  if (isNaN(end)) return null;
  const secs = (end - start) / 1000;
  if (secs <= 0) return 0;
  return secs / 86400;
}
function fmtDuration(days) {
  if (days == null) return "—";
  if (days < 1) {
    const hours = Math.max(1, Math.round(days * 24));
    return `${hours}h`;
  }
  if (days < 30) return `${Math.round(days)}d`;
  const months = days / 30;
  if (months < 12) return `${months.toFixed(1)}mo`;
  const years = days / 365;
  return `${years.toFixed(1)}y`;
}

const PHASE_CATEGORIES = {
  idea: ["idea - initial", "idea - update"],
  open: ["entry", "management", "managing", "trimmed"],
  closed: ["closed", "closed - update", "exited"],
  dead: ["dead"],
};
function matchesCategory(trade, category) {
  if (category === "all") return true;
  const phase = (trade.current_phase || "").toLowerCase().trim();
  const isClosed = trade.status === "closed";
  // A trade is "closed" in the filter sense when either the backing status is
  // closed OR the most recent phase is one of the closed phases.
  const isClosedByPhase = phase === "closed" || phase === "closed - update";
  if (category === "idea") return !isClosed && phase.startsWith("idea");
  if (category === "open") return !isClosed && phase.startsWith("open");
  if (category === "closed") return isClosed || isClosedByPhase;
  return (PHASE_CATEGORIES[category] || []).includes(phase);
}

function StatusBadge({ status, phase }) {
  const isIdea = status === "idea";
  const isClosedUpdate = status === "closed" && (phase || "").toLowerCase() === "closed - update";
  const label = isIdea && phase ? phase : (isClosedUpdate ? phase : status);
  // Any "open*" status (raw status === "open" OR a rendered label that starts
  // with "OPEN", e.g. an idea whose current_phase is "Open - Entry") uses the
  // green tone — an active position always reads as go. Closed trades stay
  // muted grey; everything else (idea variants) uses amber.
  const labelStr = String(label || "").toLowerCase();
  const isOpenLike = status === "open" || labelStr.startsWith("open");
  const cls = isOpenLike ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : status === "closed" ? "bg-zinc-800 text-zinc-400 border-zinc-700"
    : "bg-amber-500/10 text-amber-400 border-amber-500/30";
  return (
    <Badge
      className={`${cls} rounded-sm border font-mono uppercase tracking-wider whitespace-nowrap w-[9.6rem] justify-center inline-flex overflow-hidden`}
      title={label}
    >
      <span className="truncate">{label}</span>
    </Badge>
  );
}
function Stars({ value = 0, color = "blue" }) {
  const active = color === "emerald" ? "fill-emerald-500 text-emerald-500" : "fill-blue-500 text-blue-500";
  return (
    <span className="inline-flex gap-0.5">
      {[1,2,3,4,5].map((n) => (
        <Star key={n} className={`w-3 h-3 ${n <= (value || 0) ? active : "text-zinc-800"}`} />
      ))}
    </span>
  );
}

// Column definitions. Ticker is locked to position 0 and left-aligned.
// All other columns are center-aligned. Headers render UPPERCASE.
const TICKER_COL = "ticker";

function truncate(s, n = 20) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

const COLUMNS = [
  { id: "ticker", label: "TICKER", align: "left", sortValue: (t) => t.ticker || "",
    render: (t) => <Link to={`/trades/${t.id}`} className="font-semibold text-blue-400 hover:text-blue-300" data-testid={`ticker-link-${t.ticker}`}>{t.ticker}</Link> },
  { id: "company", label: "COMPANY", align: "center", sortValue: (t, ctx) => (ctx?.companies?.[t.ticker] || "").toLowerCase(),
    render: (t, ctx) => {
      const name = ctx?.companies?.[t.ticker];
      if (!name) return <span className="text-zinc-600">—</span>;
      return <span className="text-zinc-300 normal-case" title={name} data-testid={`company-${t.ticker}`}>{truncate(name, 12)}</span>;
    }
  },
  { id: "status", label: "STATUS", align: "center", sortValue: (t) => t.current_phase || t.status || "",
    render: (t) => <StatusBadge status={t.status} phase={t.current_phase} /> },
  { id: "direction", label: "DIR", align: "center", sortValue: (t) => t.direction || "",
    render: (t) => <span className="text-zinc-400">{t.direction}</span> },
  { id: "confidence", label: "CONFIDENCE", align: "center", sortValue: (t) => t.current_confidence ?? -1,
    render: (t) => <div className="flex justify-center"><Stars value={t.current_confidence} color="blue" /></div> },
  { id: "confluence", label: "CONFLUENCE", align: "center", sortValue: (t) => t.confluence ?? -1,
    render: (t) => <div className="flex justify-center"><Stars value={t.confluence} color="emerald" /></div> },
  { id: "entry", label: "AVG ENTRY", align: "center", sortValue: (t) => t.entry_avg ?? t.entry_price ?? -Infinity,
    render: (t) => {
      const v = t.entry_avg ?? t.entry_price;
      const derived = t.entry_avg != null && t.entry_avg !== t.entry_price;
      return (
        <span className="inline-flex items-center gap-1" title={derived ? "Avg of Open - Entry / Open - Added fills" : "Planned entry"}>
          {fmtPrice(v)}
        </span>
      );
    }
  },
  { id: "exit", label: "AVG EXIT", align: "center", sortValue: (t) => t.exit_avg ?? t.exit_price ?? -Infinity,
    render: (t) => {
      const v = t.exit_avg ?? t.exit_price;
      return <span title={t.exit_avg != null ? "Avg of Open - Trimmed / close fills" : ""}>{fmtPrice(v)}</span>;
    }
  },
  { id: "top_exit", label: "TOP EXIT", align: "center",
    sortValue: (t) => (typeof t.top_exit_price === "number" ? t.top_exit_price : -Infinity),
    render: (t) => {
      if (typeof t.top_exit_price !== "number") return <span className="text-zinc-600">—</span>;
      const isLong = (t.direction || "long") !== "short";
      return (
        <span
          className="font-mono text-zinc-300"
          title={isLong ? "Highest price shares were sold (Open - Trimmed / Closed)" : "Lowest price shares were bought back (Open - Trimmed / Closed)"}
          data-testid={`top-exit-${t.ticker}`}
        >
          {fmtPrice(t.top_exit_price)}
        </span>
      );
    }
  },
  { id: "live_price", label: "LIVE PRICE", align: "center",
    sortValue: (t, ctx) => {
      const p = ctx?.quotes?.[t.ticker]?.price;
      return typeof p === "number" ? p : -Infinity;
    },
    render: (t, ctx) => {
      const q = ctx?.quotes?.[t.ticker];
      if (!q || typeof q.price !== "number") return <span className="text-zinc-600" title="Fetching live price…">—</span>;
      const pct = q.change_pct;
      const tone = pct == null ? "text-zinc-300" : pct >= 0 ? "text-emerald-400" : "text-rose-400";
      return (
        <span className={`font-mono ${tone}`} title="Latest quote from yfinance" data-testid={`live-price-${t.ticker}`}>
          {fmtPrice(q.price)}
        </span>
      );
    }
  },
  { id: "size", label: "SIZE%", align: "center", sortValue: (t) => t.position_size_pct ?? -Infinity,
    render: (t) => <span>{t.position_size_pct != null ? `${t.position_size_pct}%` : "—"}</span> },
  { id: "pnl", label: "P&L DOLLARS", align: "center", sortValue: (t, ctx) => computeTotalPnl(t, ctx?.quotes) ?? t.pnl ?? -Infinity,
    render: (t, ctx) => {
      const total = computeTotalPnl(t, ctx?.quotes);
      const hasOpen = (t.open_shares || 0) > 0;
      const price = ctx?.quotes?.[t.ticker]?.price;
      if (total == null) return <span className="text-zinc-500">—</span>;
      const tone = total > 0 ? "text-emerald-500" : total < 0 ? "text-rose-500" : "text-zinc-500";
      const unrealizedPending = hasOpen && price == null;
      return (
        <span className={`${tone} inline-flex items-center gap-1`} title={hasOpen ? `Realized $${(t.realized_pnl || 0).toFixed(2)} + Unrealized @ live price` : "Realized"}>
          {total >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {fmtMoney(total)}
          {hasOpen && !unrealizedPending && <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono ml-0.5">live</span>}
          {unrealizedPending && <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono ml-0.5">…</span>}
        </span>
      );
    }
  },
  { id: "pnl_pct", label: "P&L %", align: "center", sortValue: (t, ctx) => computeTotalPnlPct(t, ctx?.quotes) ?? -Infinity,
    render: (t, ctx) => {
      const pct = computeTotalPnlPct(t, ctx?.quotes);
      if (pct == null) return <span className="text-zinc-500">—</span>;
      const tone = pct > 0 ? "text-emerald-500" : pct < 0 ? "text-rose-500" : "text-zinc-500";
      return <span className={tone}>{`${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}</span>;
    }
  },
  { id: "rr", label: "R:R", align: "center", sortValue: (t) => computeJournalRR(t) ?? -Infinity,
    render: (t) => {
      const rr = computeJournalRR(t);
      if (rr == null) return <span className="text-zinc-500">—</span>;
      const tone = rr >= 2 ? "text-emerald-400" : rr < 1 ? "text-rose-400" : "text-zinc-200";
      return <span className={tone}>1 : {rr.toFixed(2)}</span>;
    }
  },
  { id: "bq_score", label: "BQ SCORE", align: "center",
    // Business Quality total = sum of all 1-10 criterion scores (max 100).
    // Sorts numerically; trades with nothing rated fall to the bottom on desc.
    sortValue: (t) => {
      const bq = t?.business_quality;
      if (!bq) return -Infinity;
      const scores = Object.values(bq).map((v) => v?.score).filter((s) => typeof s === "number");
      return scores.length ? scores.reduce((a, b) => a + b, 0) : -Infinity;
    },
    render: (t) => {
      const bq = t?.business_quality;
      const scores = bq ? Object.values(bq).map((v) => v?.score).filter((s) => typeof s === "number") : [];
      if (scores.length === 0) return <span className="text-zinc-500">—</span>;
      const total = scores.reduce((a, b) => a + b, 0);
      const tone = total >= 70 ? "text-emerald-400" : total <= 30 ? "text-rose-400" : "text-amber-400";
      return (
        <span
          className={`font-mono ${tone}`}
          data-testid={`bq-score-${t.ticker}`}
          title={`${scores.length}/10 criteria rated`}
        >
          {total}<span className="text-zinc-600 text-[10px]">/100</span>
        </span>
      );
    }
  },
  { id: "timeframe", label: "TIMEFRAME", align: "center", sortValue: (t) => t.timeframe || "",
    render: (t) => <span className="text-zinc-400">{t.timeframe || "—"}</span> },
  { id: "date", label: "OPENED", align: "center", sortValue: (t) => t.created_at || "",
    render: (t) => <span className="text-zinc-500">{fmtDate(t.created_at)}</span> },
  { id: "duration", label: "DURATION", align: "center", sortValue: (t) => computeDurationDays(t) ?? -Infinity,
    render: (t) => {
      const days = computeDurationDays(t);
      const isOpen = t?.status !== "closed" && days != null;
      return (
        <span className="text-zinc-300 font-mono" title={isOpen ? "Open — running duration" : "Trade entry to close"} data-testid={`duration-${t.ticker}`}>
          {fmtDuration(days)}
        </span>
      );
    }
  },
  { id: "last_phase_at", label: "LAST UPDATE", align: "center", sortValue: (t) => t.last_phase_at || "",
    render: (t) => (
      <span className="text-zinc-400" title={t.last_phase_at ? new Date(t.last_phase_at).toLocaleString() : ""}>
        {fmtRelative(t.last_phase_at)}
      </span>
    )
  },
  { id: "phase_count", label: "# UPDATES", align: "center", sortValue: (t) => t.phase_count ?? 0,
    render: (t) => <span className="text-zinc-300">{t.phase_count ?? 0}</span> },
  { id: "reactions", label: "FEEDBACK", align: "center", sortValue: (t) => (t.likes_count ?? (t.likes?.length ?? 0)) + (t.profits_count ?? (t.profits?.length ?? 0)),
    render: (t, ctx) => (
      <div className="flex justify-center normal-case">
        <TradeReactions trade={t} size="sm" stopPropagation onUpdated={(r) => ctx?.onReactionUpdate?.(t.id, r)} />
      </div>
    )
  },
];

const DEFAULT_COLUMN_IDS = ["ticker","company","status","direction","confidence","confluence","entry","exit","top_exit","live_price","size","pnl","pnl_pct","rr","bq_score","timeframe","date","duration","last_phase_at","phase_count","reactions"];
const STORAGE_KEY = "zigzagx:journal:columns";

function normalizeColumnConfig(parsed) {
  const known = COLUMNS.map((c) => c.id);
  const order = (parsed?.order || []).filter((id) => known.includes(id));
  // Append any newly-added columns to the end so schema updates flow to
  // existing configs without wiping them.
  const merged = [...order, ...known.filter((id) => !order.includes(id))];
  const withoutTicker = merged.filter((id) => id !== TICKER_COL);
  const finalOrder = [TICKER_COL, ...withoutTicker];
  const storedVisible = parsed?.visible ? parsed.visible.filter((id) => known.includes(id)) : merged;
  const newColumns = known.filter((id) => !(parsed?.visible || []).includes(id) && !(parsed?.order || []).includes(id));
  const visible = Array.from(new Set([TICKER_COL, ...storedVisible, ...newColumns]));
  return { order: finalOrder, visible };
}

function loadColumnConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { order: DEFAULT_COLUMN_IDS, visible: DEFAULT_COLUMN_IDS };
    return normalizeColumnConfig(JSON.parse(raw));
  } catch {
    return { order: DEFAULT_COLUMN_IDS, visible: DEFAULT_COLUMN_IDS };
  }
}

export default function Journal() {
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const dollarsAllowed = canSeeDollarStats(user);
  const [trades, setTrades] = useState([]);
  const [settings, setSettings] = useState(null);
  const [companies, setCompanies] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zigzagx:companies") || "{}"); } catch { return {}; }
  });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [followedOnly, setFollowedOnly] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState("all");
  // Default sort: newest updates on top for the FIRST view each login session.
  // We use sessionStorage as the marker so it resets on logout / new browser
  // session but persists across in-app navigation (hopping to Global Stats
  // and back doesn't re-apply the default over the user's active sort).
  const [sort, setSort] = useState(() => {
    try {
      const saved = sessionStorage.getItem("zzx.journal.sort");
      if (saved) return JSON.parse(saved);
    } catch { /* private mode, storage unavailable */ }
    // First mount this session → most-recently-updated on top.
    return { id: "last_phase_at", dir: "desc" };
  });

  // Persist the active sort for the rest of the session so revisiting the
  // Journal keeps whatever the user last picked (still resets next login).
  useEffect(() => {
    try { sessionStorage.setItem("zzx.journal.sort", JSON.stringify(sort)); }
    catch { /* ignore storage failures */ }
  }, [sort]);
  const [colConfig, setColConfig] = useState(loadColumnConfig);
  const [dragId, setDragId] = useState(null);
  const [quotes, setQuotes] = useState({}); // { TICKER: {price, ...} }
  // Hydrate column layout from the user's stored preference on mount so
  // configs persist across devices / logins. Local state stays authoritative
  // once loaded so drags & toggles feel instant.
  const hydratedRef = useRef(false);
  // Tracks the config we last successfully sent to the server, so an unmount
  // mid-debounce can flush any un-saved diff before the component tears down
  // (fixes: "navigating away right after a drag lost my column change").
  const lastSavedRef = useRef(null);

  const saveColConfig = (cfg) => {
    lastSavedRef.current = cfg;
    // Keep AuthContext.user.column_config in sync so the next Journal mount
    // hydrates from the fresh value instead of the stale one from login.
    if (patchUser) patchUser({ column_config: cfg });
    api.patch("/auth/me", { column_config: cfg }).catch(() => {});
  };

  useEffect(() => {
    if (hydratedRef.current || !user || user === false) return;
    // Wait until the full profile has been fetched (column_config field
    // present on the response, even if null). The initial login/register
    // response is a slim subset and doesn't include it.
    if (!("column_config" in user)) return;
    if (user.column_config && (user.column_config.order || user.column_config.visible)) {
      const normalized = normalizeColumnConfig(user.column_config);
      setColConfig(normalized);
      lastSavedRef.current = normalized;
    } else {
      // First time this user has a server-side layout — persist whatever they
      // have locally so it becomes the source of truth (and, for the founding
      // super_admin, the template new invitees inherit).
      saveColConfig(colConfig);
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(colConfig)); } catch { /* ignore */ }
    // Skip the very first write so we don't clobber the server value with the
    // default before hydration runs.
    if (!hydratedRef.current) return;
    const serialized = JSON.stringify(colConfig);
    const lastSerialized = JSON.stringify(lastSavedRef.current);
    if (serialized === lastSerialized) return; // no diff → no PATCH
    const t = setTimeout(() => { saveColConfig(colConfig); }, 600);
    return () => {
      clearTimeout(t);
      // If we're unmounting with a pending un-saved diff (e.g. user dragged
      // a column and immediately navigated away), fire the PATCH now so the
      // change survives the nav.
      if (JSON.stringify(lastSavedRef.current) !== serialized) {
        saveColConfig(colConfig);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colConfig]);

  const load = async () => {
    const [t, s] = await Promise.all([api.get("/trades"), api.get("/settings")]);
    setTrades(t.data);
    setSettings(s.data);
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("trades-changed", h);
    return () => window.removeEventListener("trades-changed", h);
  }, []);

  // Fetch current prices for every ticker in the journal so the P&L column
  // and the LIVE PRICE column both stay fresh — not just open positions.
  useEffect(() => {
    const allTickers = Array.from(new Set(trades.map((t) => t.ticker).filter(Boolean)));
    if (allTickers.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/market/quotes`, { tickers: allTickers });
        if (cancelled) return;
        setQuotes((prev) => ({ ...prev, ...data }));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [trades]);

  // Fetch company names for any tickers we don't have a name for yet.
  // Empty-string values are treated as "not yet resolved" so a transient
  // backend miss (e.g. Yahoo throttling) doesn't stick permanently in the
  // localStorage cache.
  useEffect(() => {
    const missing = Array.from(new Set(trades.map((t) => t.ticker))).filter((tk) => tk && !companies[tk]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(missing.map(async (tk) => {
        try {
          const r = await api.get(`/market/company/${tk}`);
          return [tk, r.data?.name || ""];
        } catch { return [tk, ""]; }
      }));
      if (cancelled) return;
      setCompanies((prev) => {
        const next = { ...prev };
        let dirty = false;
        for (const [tk, name] of results) {
          // Only overwrite when we actually got a name back — leaves empty
          // slots eligible for a retry on the next mount / trade change.
          if (name && next[tk] !== name) {
            next[tk] = name;
            dirty = true;
          }
        }
        if (dirty) {
          try { localStorage.setItem("zigzagx:companies", JSON.stringify(next)); } catch { /* ignore */ }
        }
        return dirty ? next : prev;
      });
    })();
    return () => { cancelled = true; };
  }, [trades]); // eslint-disable-line react-hooks/exhaustive-deps

  const availablePhases = Array.from(new Set([
    ...(settings?.default_phases || []),
    ...trades.map((t) => t.current_phase).filter(Boolean),
  ]));

  const filtered = trades.filter((t) => {
    if (!matchesCategory(t, filter)) return false;
    if (phaseFilter !== "all" && (t.current_phase || "") !== phaseFilter) return false;
    if (followedOnly && !(user?.id && (t.followers || []).includes(user.id))) return false;
    if (q && !t.ticker.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const sortedTrades = useMemo(() => {
    const col = COLUMNS.find((c) => c.id === sort.id);
    if (!col) return filtered;
    const dir = sort.dir === "asc" ? 1 : -1;
    const ctx = { companies, quotes };
    return [...filtered].sort((a, b) => {
      const av = col.sortValue(a, ctx);
      const bv = col.sortValue(b, ctx);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sort, companies, quotes]);

  const handleReactionUpdate = (tradeId, r) => {
    setTrades((prev) => prev.map((t) => (t.id === tradeId ? { ...t, ...r } : t)));
  };
  const rowCtx = { companies, onReactionUpdate: handleReactionUpdate, quotes };

  const orderedVisibleColumns = colConfig.order
    .filter((id) => colConfig.visible.includes(id))
    .filter((id) => dollarsAllowed || id !== "pnl")
    .map((id) => COLUMNS.find((c) => c.id === id))
    .filter(Boolean);

  const toggleSort = (id) => {
    setSort((s) => (s.id === id ? { id, dir: s.dir === "asc" ? "desc" : "asc" } : { id, dir: "asc" }));
  };

  const onDragStart = (id) => {
    if (id === TICKER_COL) return; // Ticker is locked to position 0.
    setDragId(id);
  };
  const onDragOverCol = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    if (targetId === TICKER_COL) return; // Cannot drop into ticker's locked slot.
    setColConfig((c) => {
      const order = [...c.order];
      const from = order.indexOf(dragId);
      const to = order.indexOf(targetId);
      if (from < 0 || to < 0) return c;
      order.splice(from, 1);
      order.splice(to, 0, dragId);
      // Enforce ticker at position 0.
      const withoutTicker = order.filter((id) => id !== TICKER_COL);
      return { ...c, order: [TICKER_COL, ...withoutTicker] };
    });
  };
  const onDragEnd = () => setDragId(null);

  const toggleVisible = (id) => {
    if (id === TICKER_COL) return; // Ticker cannot be hidden.
    setColConfig((c) => ({
      ...c,
      visible: c.visible.includes(id) ? c.visible.filter((x) => x !== id) : [...c.visible, id],
    }));
  };
  const resetColumns = () => setColConfig({ order: DEFAULT_COLUMN_IDS, visible: DEFAULT_COLUMN_IDS });

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="page-title">Trade Journal</h1>
          <p className="text-sm text-zinc-500">All the Ideas, Strategies, and Trades.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticker" data-testid="journal-search" className="bg-zinc-950 border-zinc-800 rounded-sm pl-9 font-mono" />
        </div>
        <button
          type="button"
          onClick={() => setFollowedOnly((v) => !v)}
          aria-pressed={followedOnly}
          aria-label="Show only trades you follow"
          data-testid="filter-followed-toggle"
          title={followedOnly ? "Showing only trades you follow — click to see all" : "Show only trades you follow"}
          className={`h-9 w-9 inline-flex items-center justify-center rounded-sm border transition-colors ${
            followedOnly
              ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-400"
              : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-zinc-200 hover:border-zinc-700"
          }`}
        >
          <Bell className={`w-4 h-4 ${followedOnly ? "fill-white" : ""}`} />
        </button>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm h-9">
            <TabsTrigger value="all" data-testid="filter-all" className="rounded-sm text-xs data-[state=active]:bg-zinc-800">All</TabsTrigger>
            <TabsTrigger value="idea" data-testid="filter-idea" className="rounded-sm text-xs data-[state=active]:bg-zinc-800">Ideas</TabsTrigger>
            <TabsTrigger value="open" data-testid="filter-open" className="rounded-sm text-xs data-[state=active]:bg-zinc-800">Open</TabsTrigger>
            <TabsTrigger value="closed" data-testid="filter-closed" className="rounded-sm text-xs data-[state=active]:bg-zinc-800">Closed</TabsTrigger>
            <TabsTrigger value="dead" data-testid="filter-dead" className="rounded-sm text-xs data-[state=active]:bg-zinc-800">Dead</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={phaseFilter} onValueChange={setPhaseFilter}>
          <SelectTrigger data-testid="phase-filter" className="bg-zinc-950 border-zinc-800 rounded-sm h-9 w-48 text-xs">
            <SelectValue placeholder="All Phases" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all" data-testid="phase-filter-all">All Phases</SelectItem>
            {availablePhases.map((p) => (
              <SelectItem key={p} value={p} data-testid={`phase-filter-${p}`}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {phaseFilter !== "all" && (
          <button onClick={() => setPhaseFilter("all")} data-testid="clear-phase-filter" className="text-xs text-zinc-500 hover:text-zinc-300 font-mono uppercase tracking-wider">
            Clear
          </button>
        )}

        <div className="ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="columns-btn" className="h-9 rounded-sm border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-300 gap-2">
                <Columns3 className="w-4 h-4" /> Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 bg-[#0a0a0a] border-zinc-800 rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-400">Columns</span>
                <button onClick={resetColumns} data-testid="reset-columns" className="text-[10px] font-mono uppercase text-zinc-500 hover:text-zinc-200">Reset</button>
              </div>
              <p className="text-[10px] text-zinc-600 mb-2">Drag to reorder · Click to toggle visibility</p>
              <div className="space-y-1 max-h-80 overflow-y-auto">
                {colConfig.order.map((id) => {
                  const col = COLUMNS.find((c) => c.id === id);
                  if (!col) return null;
                  if (!dollarsAllowed && id === "pnl") return null;
                  const visible = colConfig.visible.includes(id);
                  const locked = id === TICKER_COL;
                  return (
                    <div
                      key={id}
                      draggable={!locked}
                      onDragStart={() => onDragStart(id)}
                      onDragOver={(e) => onDragOverCol(e, id)}
                      onDragEnd={onDragEnd}
                      data-testid={`col-config-${id}`}
                      className={`flex items-center gap-2 px-2 py-1 rounded-sm border ${dragId === id ? "border-blue-500 bg-blue-500/10" : "border-transparent hover:bg-zinc-900"} ${locked ? "cursor-not-allowed opacity-70" : "cursor-move"}`}
                    >
                      <GripVertical className={`w-3.5 h-3.5 ${locked ? "text-zinc-800" : "text-zinc-600"}`} />
                      <Checkbox
                        checked={visible}
                        disabled={locked}
                        onCheckedChange={() => toggleVisible(id)}
                        className="rounded-sm border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                        data-testid={`col-toggle-${id}`}
                      />
                      <span className="text-xs text-zinc-200 flex-1">{col.label}</span>
                      {locked && <span className="text-[9px] font-mono uppercase text-zinc-600">Locked</span>}
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-900 hover:bg-transparent">
              {orderedVisibleColumns.map((col) => {
                const active = sort.id === col.id;
                const alignCls = col.align === "left" ? "text-left" : col.align === "right" ? "text-right" : "text-center";
                const btnJustify = col.align === "left" ? "justify-start" : col.align === "right" ? "justify-end" : "justify-center";
                return (
                  <TableHead key={col.id} className={`text-xs uppercase tracking-wider font-mono text-zinc-500 ${alignCls}`}>
                    <button
                      onClick={() => toggleSort(col.id)}
                      data-testid={`sort-${col.id}`}
                      className={`inline-flex items-center gap-1 w-full ${btnJustify} hover:text-zinc-200 ${active ? "text-zinc-100" : ""}`}
                    >
                      {col.label}
                      {active ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTrades.length === 0 && (
              <TableRow className="border-zinc-900 hover:bg-transparent">
                <TableCell colSpan={orderedVisibleColumns.length || 1} className="text-center text-zinc-600 py-12 text-sm">
                  No trades yet. Click <span className="text-blue-500">New Trade</span> to add one.
                </TableCell>
              </TableRow>
            )}
            {sortedTrades.map((t) => (
              <TableRow
                key={t.id}
                className="border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
                data-testid={`row-${t.ticker}`}
                onClick={() => navigate(`/trades/${t.id}`)}
              >
                {orderedVisibleColumns.map((col) => {
                  const alignCls = col.align === "left" ? "text-left" : col.align === "right" ? "text-right" : "text-center";
                  return (
                    <TableCell key={col.id} className={`py-2 text-xs uppercase font-mono ${alignCls}`}>
                      {col.render(t, rowCtx)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
